import { Navigate, useNavigate } from "react-router-dom";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";
import { 
  FileText, Download, Clock, CheckCircle2, AlertTriangle, ShieldAlert, 
  ArrowRight, FileArchive, Layers, BarChart3, AlertCircle, Info, Box, Target,
  Calendar, TrendingUp, TrendingDown, ShieldCheck, RefreshCw, Search, Activity, Zap, Sparkles,
  Gauge, Cpu, Database, Eye, History, Loader2, PieChart as PieChartIcon
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area } from "recharts";
import { Button } from "@/components/ui/button";
import { exportDashboardPdf } from "@/lib/export-pdf";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { useEffect, useMemo, useState } from "react";
import { maskName } from "@/lib/format";
import { useLanguage } from "@/contexts/LanguageContext";

export default function DashboardPage() {
  const { dashboard, notas, duracaoMs, divergencias, privacyMode, reprocessarPorCnpj } = useFiscalStore();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [volumetriaDays, setVolumetriaDays] = useState<number>(31);
  const [manualCnpj, setManualCnpj] = useState("");
  const [ledgerType, setLedgerType] = useState<"ENTRADA" | "SAIDA">("SAIDA");

  useEffect(() => { document.title = "Executive Intelligence — DataGrid XML"; }, []);

  const stats = dashboard?.stats || { totalNotas: 0, totalRevenue: 0 };
  
  const totalRevenue = stats.totalRevenue;
  const totalNotas = stats.totalNotas;
  const ticketMedio = totalNotas > 0 ? totalRevenue / totalNotas : 0;
  const cnpjEmpresa = dashboard?.cnpjEmpresa || "";
  const nomeEmpresa = dashboard?.nomeEmpresa || "";

  const memoData = useMemo(() => {
    return {
      chartPeriodo: dashboard?.chartTimeline || [],
      cfopDataEntrada: dashboard?.cfopDataEntrada || [],
      cfopDataSaida: dashboard?.cfopDataSaida || [],
      saneamento: dashboard?.saneamento || { totalItens: 0, ncmGenerico: 0, eanInvalido: 0, semCest: 0, score: 100 },
      economiaEstimada: divergencias.reduce((acc, d) => d.diferenca < 0 ? acc + Math.abs(d.diferenca) : acc, 0),
    };
  }, [dashboard, divergencias]);

  const { taxEntrada, taxSaida } = useMemo(() => {
    const cardsE = dashboard?.taxCardsE || [];
    const cardsS = dashboard?.taxCardsS || [];
    const totalE = cardsE.reduce((s, c) => s + c.totalValue, 0);
    const totalS = cardsS.reduce((s, c) => s + c.totalValue, 0);
    const revE = dashboard?.operacoesSummary?.entradas?.valorTotal || 0;
    const revS = dashboard?.operacoesSummary?.saidas?.valorTotal || 0;

    return {
      taxEntrada: { cards: cardsE, total: totalE, revenue: revE, carga: revE > 0 ? (totalE / revE) * 100 : 0 },
      taxSaida: { cards: cardsS, total: totalS, revenue: revS, carga: revS > 0 ? (totalS / revS) * 100 : 0 }
    };
  }, [dashboard]);

  const activeCfopData = ledgerType === "ENTRADA" ? memoData.cfopDataEntrada : memoData.cfopDataSaida;

  const totCfop = useMemo(() => activeCfopData.reduce((acc, r) => {
    const row = r as Record<string, unknown>;
    acc.vConsolidado += ((row.vConsolidado as number) ?? (row.vContabil as number) ?? 0);
    acc.icms += ((row.icms as number) ?? 0);
    acc.fcp += ((row.fcp as number) ?? 0);
    acc.icmsST += ((row.icmsST as number) ?? 0);
    acc.fcpST += ((row.fcpST as number) ?? 0);
    return acc;
  }, { vConsolidado: 0, icms: 0, fcp: 0, icmsST: 0, fcpST: 0 }), [activeCfopData]);

  const pieData = [
    { name: "Autorizadas", value: dashboard?.statusSummary?.autorizadas?.quantidade || 0, color: "#10B981" },
    { name: "Contingência", value: dashboard?.statusSummary?.contingencia?.quantidade || 0, color: "#FF7A00" },
    { name: "Canceladas", value: dashboard?.statusSummary?.canceladas?.quantidade || 0, color: "#EF4444" },
  ].filter(d => d.value > 0);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      try {
        if (dashboard) exportDashboardPdf(dashboard, notas, duracaoMs);
        toast.success("Intelligence Report Generated Successfully.");
      } catch (e: any) { toast.error("Export Failed."); }
      finally { setExporting(false); }
    }, 50);
  };

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-1000">
      {/* Executive Command Header */}
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-6 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Activity className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Executive Intelligence Hub</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Dashboard <span className="text-primary italic">Analytics</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
            <span className="flex items-center gap-2 uppercase tabular-nums"><Database className="size-3.5 text-primary" /> Engine: DataGrid v2.5.0</span>
            <span className="flex items-center gap-2 uppercase tabular-nums"><History className="size-3.5 text-muted-foreground" /> Last Sync: {new Date().toLocaleTimeString()}</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleExport} disabled={exporting} className="btn-primary h-12 rounded-2xl group shadow-primary/20">
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            <span className="ml-2 uppercase tracking-widest text-[10px] font-black">Exportar Intelligence PDF</span>
          </Button>
        </div>
      </header>

      {/* Identity & Audit Center Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        <div className="lg:col-span-8 glass-card p-10 flex flex-col justify-between border-primary/10 relative overflow-hidden group bg-white/[0.01]">
          <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:scale-110 transition-transform duration-700">
            <Zap className="size-48 rotate-12" />
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="size-16 rounded-[2rem] bg-primary flex items-center justify-center shadow-2xl shadow-primary/40 group-hover:rotate-6 transition-transform">
                <ShieldCheck className="size-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-black uppercase tracking-widest text-primary">Entidade em Inspeção</span>
                  <div className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                </div>
                <h2 className="text-4xl font-black truncate tracking-tighter leading-none">
                  {nomeEmpresa ? (privacyMode ? maskName(nomeEmpresa) : nomeEmpresa) : "Intelligence Console"}
                </h2>
                <div className="flex items-center gap-4 mt-3">
                  <div className="px-3 py-1 rounded-lg glass-technical border-primary/20 text-[10px] font-black text-primary uppercase tabular-nums">
                    {cnpjEmpresa ? (privacyMode ? "**.***/****-**" : `CNPJ ${cnpjEmpresa}`) : "NO SOURCE IDENTIFIED"}
                  </div>
                  <div className="text-[9px] font-black text-muted-foreground/60 uppercase tracking-[0.2em] flex items-center gap-1.5">
                    <ShieldCheck className="size-3 text-emerald-500" /> AES-GCM Encrypted
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-12 pt-12 border-t border-foreground/[0.03]">
              <MiniStat label="Volume Processado" value={fmtNum(totalNotas)} sub="Documentos XML" trend={dashboard?.stats?.volumeTrend} />
              <MiniStat label="Receita Bruta" value={fmtBRL(totalRevenue)} sub="Ciclo Autorizado" trend={dashboard?.stats?.revenueTrend} />
              <MiniStat label="Ticket Médio" value={fmtBRL(ticketMedio)} sub="Valor por Nota" trend={0} />
              <MiniStat label="Custo Operacional" value={fmtBRL(dashboard?.operacoesSummary?.entradas?.valorTotal || 0)} sub="Inbound Supply" trend={0} />
            </div>
          </div>
        </div>

        {/* Audit Alert Center */}
        <div className="lg:col-span-4 glass-card p-8 border-red-500/10 bg-red-500/[0.02] flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 text-red-500">
                <ShieldAlert className="size-4" /> Audit Alert Center
              </h3>
              <div className="flex items-center gap-2">
                 <span className="text-[10px] font-black text-red-500/60 uppercase">Live</span>
                 <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              </div>
            </div>
            <div className="space-y-4">
               <AlertItem icon={AlertTriangle} label="Divergências Críticas" value={dashboard?.riskSummary.alta || 0} color="text-red-500" bg="bg-red-500/10" />
               <AlertItem icon={Info} label="Inconsistências Médias" value={dashboard?.riskSummary.media || 0} color="text-amber-500" bg="bg-amber-500/10" />
               <AlertItem icon={CheckCircle2} label="Conformidade Fiscal" value={`${memoData.saneamento.score.toFixed(0)}%`} color="text-emerald-500" bg="bg-emerald-500/10" />
               <AlertItem icon={Activity} label="Engine Health" value="99.8%" color="text-primary" bg="bg-primary/10" />
            </div>
          </div>
          <div className="mt-8 p-4 rounded-2xl glass-technical border-red-500/10 bg-red-500/[0.03]">
             <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground leading-relaxed">
               A engine de auditoria identificou <span className="text-red-500">{(dashboard?.riskSummary.alta || 0) + (dashboard?.riskSummary.media || 0)}</span> pontos de atenção. <span className="text-foreground">Revisão manual sugerida.</span>
             </p>
          </div>
        </div>
      </div>

      {/* Main Stats Bento Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Status SEFAZ */}
        <div className="lg:col-span-4 glass-card p-8 flex flex-col border-primary/5 bg-white/[0.01]">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
                <Zap className="size-4 text-primary" /> Matriz Operacional
              </h3>
              <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Status SEFAZ em Tempo Real</p>
            </div>
            <PieChartIcon className="size-4 text-muted-foreground opacity-20" />
          </div>
          
          <div className="flex-1 min-h-[200px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} innerRadius={70} outerRadius={90} dataKey="value" stroke="none" paddingAngle={10}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} className="outline-none" />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.95)', border: 'none', borderRadius: '16px', color: 'white', fontSize: '11px', fontWeight: 'bold' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-4xl font-black tabular-nums tracking-tighter leading-none">{fmtNum(totalNotas)}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mt-1">Total XML</span>
            </div>
          </div>

          <div className="mt-8 space-y-2">
            {pieData.map(d => (
              <div key={d.name} className="flex items-center justify-between p-3.5 rounded-2xl glass-technical border-foreground/[0.02] hover:border-primary/20 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="size-2 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors">{d.name}</span>
                </div>
                <span className="text-xs font-black tabular-nums">{fmtNum(d.value)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8 glass-card p-8 flex flex-col border-primary/5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
                <Activity className="size-4 text-primary" /> Volumetria de Pipeline
              </h3>
              <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Série Temporal de Ingestão de Dados</p>
            </div>
            <div className="flex items-center gap-1 glass-technical p-1.5 rounded-xl border-foreground/5">
              {[7, 15, 31].map((d) => (
                <button
                  key={d}
                  onClick={() => setVolumetriaDays(d)}
                  className={cn(
                    "px-6 py-2 text-[10px] font-black uppercase tracking-tighter rounded-lg transition-all",
                    volumetriaDays === d ? "bg-primary text-white shadow-xl shadow-primary/20" : "text-muted-foreground hover:bg-primary/5"
                  )}
                >
                  {d === 31 ? "30 Dias" : `${d} Dias`}
                </button>
              ))}
            </div>
          </div>

          <div className="h-[300px] flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={memoData.chartPeriodo.slice(-volumetriaDays)}>
                <defs>
                  <linearGradient id="colorEntrada" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSaida" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF7A00" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#FF7A00" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fontWeight: 900 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fontWeight: 900 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '16px', color: 'white' }} />
                <Area type="monotone" dataKey="Entrada" stroke="#10B981" strokeWidth={4} fillOpacity={1} fill="url(#colorEntrada)" />
                <Area type="monotone" dataKey="Saida" stroke="#FF7A00" strokeWidth={4} fillOpacity={1} fill="url(#colorSaida)" />
                <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '30px', fontSize: '9px', fontWeight: '900', textTransform: 'uppercase' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-8 pt-8 border-t border-foreground/[0.03] grid grid-cols-2 sm:grid-cols-4 gap-8">
             <EngineStat label="Pico Processado" value={fmtNum(dashboard?.stats?.peakProcessed || 0)} unit="XML/D" />
             <EngineStat label="Avg Throughput" value={fmtNum(dashboard?.stats?.avgThroughput || 0)} unit="XML/D" />
             <EngineStat label="Engine Latency" value={`${(duracaoMs / Math.max(1, totalNotas)).toFixed(1)}ms`} />
             <EngineStat label="Active Threads" value={`${Math.min(8, navigator.hardwareConcurrency || 4)} Workers`} />
          </div>
        </div>
      </div>

      {/* Quick Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <QuickLink title="Métricas de Impostos" desc="Analise créditos e débitos de ICMS, PIS e COFINS." icon={Zap} path="/insights" color="bg-primary" />
        <QuickLink title="Cruzamento de Dados" desc="Curva ABC, RFM e Anomalias de Preço." icon={Target} path="/analytics" color="bg-emerald-500" />
        <QuickLink title="Análise de Produtos" desc="Ranking de SKUs e Carga Tributária por Item." icon={Box} path="/produtos" color="bg-blue-500" />
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, trend }: any) {
  const isPositive = trend && trend > 0;
  return (
    <div className="flex flex-col gap-1 group/stat">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-60">{label}</span>
        {trend && (
          <div className={cn("flex items-center gap-0.5 text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded-md", isPositive ? "text-emerald-500 bg-emerald-500/10" : "text-red-500 bg-red-500/10")}>
            {isPositive ? <TrendingUp className="size-2.5" /> : <TrendingDown className="size-2.5" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <span className="text-xl font-black tabular-nums leading-none tracking-tighter group-hover/stat:text-primary transition-colors">{value}</span>
      <span className="text-[8px] font-bold text-muted-foreground/40 uppercase tracking-tight">{sub}</span>
    </div>
  );
}

function AlertItem({ icon: Icon, label, value, color, bg }: any) {
  return (
    <div className={cn("flex items-center justify-between p-4 rounded-2xl border border-white/5 transition-all hover:scale-[1.02]", bg)}>
      <div className="flex items-center gap-3">
        <Icon className={cn("size-4", color)} />
        <span className="text-[10px] font-black uppercase tracking-widest opacity-80">{label}</span>
      </div>
      <span className={cn("text-lg font-black tabular-nums", color)}>{value}</span>
    </div>
  );
}

function EngineStat({ label, value, unit }: any) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[8px] font-black text-muted-foreground uppercase tracking-widest opacity-60">{label}</span>
      <span className="text-xl font-black tabular-nums leading-none tracking-tighter">
        {value} {unit && <span className="text-[10px] text-primary">{unit}</span>}
      </span>
    </div>
  );
}

function QuickLink({ title, desc, icon: Icon, path, color }: any) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(path)} className="glass-card p-6 text-left hover:border-primary/20 transition-all group relative overflow-hidden">
      <div className={cn("size-12 rounded-2xl flex items-center justify-center text-white mb-4 shadow-lg", color)}>
        <Icon className="size-6" />
      </div>
      <h3 className="text-lg font-black tracking-tighter mb-1">{title}</h3>
      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight">{desc}</p>
      <ArrowRight className="size-4 absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
    </button>
  );
}
